import { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { Button, Input, Spin, message, Card, Result, Select, Space } from 'antd'
import { SafetyCertificateOutlined } from '@ant-design/icons'
import axios from 'axios'

const API = 'https://blog.bjk-med.com/api'

export default function MiniSignPage() {
  const { orderNo } = useParams()
  const [loading, setLoading] = useState(true)
  const [order, setOrder] = useState(null)
  const [signed, setSigned] = useState(false)
  const [signers, setSigners] = useState([])
  const [selectedSigner, setSelectedSigner] = useState(null)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState('')
  const [remark, setRemark] = useState('')
  const [signing, setSigning] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => { if (orderNo) fetchOrder() }, [orderNo])

  const fetchOrder = async () => {
    try {
      const { data } = await axios.get(`${API}/mini/order-detail`, { params: { order_no: orderNo } })
      if (data.success) {
        setOrder(data.data)
        setSigned(data.signed)
        setSigners(data.signers || [])
        if (data.signers?.length === 1) setSelectedSigner(data.signers[0].id)
      } else {
        setResult({ success: false, message: data.message })
      }
    } catch {
      setResult({ success: false, message: '网络错误，请重试' })
    } finally { setLoading(false) }
  }

  const handleSign = async () => {
    if (!selectedSigner) return message.warning('请选择签收人')
    if (!password) return message.warning('请输入签收密码')
    const cur = signers.find(s => s.id === selectedSigner)
    if (cur && !cur.has_pwd) return message.warning('该签收人尚未设置签收密码，请联系管理员')
    setSigning(true)
    try {
      const { data } = await axios.post(`${API}/mini/sign`, {
        order_no: orderNo, signer_id: selectedSigner, password, remark
      })
      if (data.success) {
        setPasswordError('')
        setResult(data)
      } else {
        // 密码错误：在输入框下方提示
        if (data.message && data.message.includes('密码不正确')) {
          setPasswordError('签收密码不正确，请重新输入！')
          setPassword('')
        } else {
          message.error(data.message || '签收失败')
        }
      }
    } catch {
      setResult({ success: false, message: '签收失败，请重试' })
    } finally { setSigning(false) }
  }

  const handleClose = () => {
    // 微信内置浏览器关闭 WebView
    if (typeof WeixinJSBridge !== 'undefined') {
      WeixinJSBridge.call('closeWindow')
    } else {
      window.close()
    }
  }

  if (loading) return <div style={{ textAlign: 'center', paddingTop: '40vh' }}><Spin size="large" tip="加载中..." /></div>

  if (signed) return <Result status="warning" title="该单已签收" subTitle={`发货单号：${orderNo}`}
    extra={<Button onClick={handleClose}>关闭</Button>}
  />

  if (!loading && signers.length === 0 && !signed) return (
    <Result status="warning" title="无可用签收人"
      subTitle="该客户暂无已完成认证且拥有印章的签收人，请联系管理员" />
  )

  if (result) return (
    <Result status={result.success ? 'success' : 'error'}
      title={result.success ? '签收成功' : '签收失败'}
      subTitle={result.success ? `${result.signer_name || ''} 签收完成` : result.message}
      extra={result.success ? (
        <Space>
          <Button type="primary" onClick={handleClose}>关闭</Button>
        </Space>
      ) : <Button type="primary" onClick={() => setResult(null)}>返回重试</Button>}
    />
  )

  return (
    <div style={{ maxWidth: 420, margin: '0 auto', padding: 16, paddingBottom: 40 }}>
      <Card title="发货单信息" style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, color: '#666', lineHeight: 2 }}>
          <div><b>发货单号：</b>{orderNo}</div>
          <div><b>收货单位：</b>{order?.收货单位}</div>
          <div><b>收货地址：</b>{order?.收货地址 || '-'}</div>
          <div><b>发货日期：</b>{order?.发货日期}</div>
          <div><b>仓库：</b>{order?.仓库 || '-'}</div>
          {order?.备注 && <div><b>备注：</b>{order.备注}</div>}
        </div>
      </Card>

      <Card title={`产品明细（${order?.items?.length || 0}项）`} style={{ marginBottom: 16 }}>
        {(order?.items || []).map((it, i) => (
          <div key={i} style={{ borderBottom: '1px solid #f0f0f0', padding: '8px 0', fontSize: 13 }}>
            <div style={{ fontWeight: 500 }}>{it.产品名称}</div>
            <div style={{ color: '#999', fontSize: 12 }}>{it.规格型号} | 批号 {it.批号} | {it.明细数量}{it.明细单位}</div>
          </div>
        ))}
      </Card>

      <Card title="签收确认" style={{ textAlign: 'center' }}>
        <SafetyCertificateOutlined style={{ fontSize: 40, color: '#1677ff', marginBottom: 16 }} />

        <div style={{ marginBottom: 16 }}>
          <div style={{ color: '#666', marginBottom: 8, fontSize: 14 }}>选择签收人</div>
          <Select placeholder="请选择签收人" value={selectedSigner} onChange={setSelectedSigner}
            style={{ width: '80%' }} options={signers.map(s => ({ label: s.has_pwd ? s.name : `${s.name}（未设密码）`, value: s.id }))} />
        </div>

        <div style={{ marginBottom: 16 }}>
          <Input.Password placeholder="输入签收密码" value={password}
            status={passwordError ? 'error' : undefined}
            onChange={e => { setPassword(e.target.value); if (passwordError) setPasswordError('') }}
            style={{ width: '80%' }} />
          <div style={{ color: passwordError ? '#ff4d4f' : '#999', fontSize: 12, marginTop: 4 }}>
            {passwordError || '请输入该签收人设置的签收密码'}
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <Input.TextArea placeholder="签收备注（选填）" value={remark}
            onChange={e => setRemark(e.target.value)} rows={2} maxLength={200} showCount />
        </div>

        <Button type="primary" size="large" block loading={signing}
          disabled={!selectedSigner || !password} onClick={handleSign}>
          确认签收
        </Button>
      </Card>
    </div>
  )
}
